import type { AuditLogEntry } from '@core/api/auditLog';
import { AuditAction } from '@core/enum/auditAction';
import { formatAuditSubjectDescription } from '@core/utils/auditLog';

const ITEM_CATEGORY: Record<string, string> = {
  ChasubleItem: 'Chasuble',
  AlbItem: 'Alb',
  StoleItem: 'Stole',
  MitreItem: 'Mitre',
};

const NOTE_NOISE_FIELDS = new Set(['productionProgress', 'productionSummary']);

const COLOR_FIELDS = new Set(['bodyColor', 'panelColor']);

type ChangeValue = { old: unknown; new: unknown };

type ItemChanges = Record<string, ChangeValue>;

export type ItemEditSentence = {
  label: string;
  action: string;
  color: string | null;
  actionLabel?: string;
  actionColor?: string | null;
};

export function isVestmentItemType(subjectType: string): boolean {
  return Object.prototype.hasOwnProperty.call(ITEM_CATEGORY, subjectType);
}

export function vestmentNoun(subjectType: string): string | null {
  const category = ITEM_CATEGORY[subjectType];
  return category ? category.toLowerCase() : null;
}

export function vestmentAction(
  subjectType: string,
  verb: string,
  queued = false
): string {
  const noun = vestmentNoun(subjectType);
  const line = noun ? `${verb} ${noun}` : verb;
  return queued ? `${line} and queued for printing` : line;
}

function entityLabel(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('label' in value)) {
    return null;
  }
  const label = (value as { label: unknown }).label;
  if (typeof label !== 'string') {
    return null;
  }
  const trimmed = label.trim();
  return trimmed === '' ? null : trimmed;
}

function changesOf(entry: AuditLogEntry): ItemChanges {
  return (entry.changes ?? {}) as ItemChanges;
}

function storedMetadataString(
  entry: AuditLogEntry,
  key: string
): string | null {
  const value = entry.metadata?.[key];
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function readHistoryLabel(entry: AuditLogEntry): string | null {
  return storedMetadataString(entry, 'historyLabel');
}

export function readHistoryLabelAfter(entry: AuditLogEntry): string | null {
  return storedMetadataString(entry, 'historyLabelAfter');
}

export function readHistoryColor(entry: AuditLogEntry): string | null {
  return storedMetadataString(entry, 'historyColor');
}

export function readHistoryColorAfter(entry: AuditLogEntry): string | null {
  return storedMetadataString(entry, 'historyColorAfter');
}

export function historySubject(entry: AuditLogEntry): string {
  const label = readHistoryLabel(entry);
  if (label) {
    return label;
  }
  const { typeLabel, idPart } = formatAuditSubjectDescription(entry);
  return `${typeLabel}${idPart}`;
}

function itemIdsOf(entry: AuditLogEntry): number[] {
  const itemIds = entry.metadata?.itemIds;
  if (!Array.isArray(itemIds)) {
    return [];
  }
  return itemIds.filter((id): id is number => typeof id === 'number');
}

export function readItemLabels(entry: AuditLogEntry): string[] | null {
  const itemIds = itemIdsOf(entry);
  const labels = entry.metadata?.itemLabels;
  if (
    !Array.isArray(labels) ||
    labels.length !== itemIds.length ||
    itemIds.length === 0
  ) {
    return null;
  }
  const names: string[] = [];
  for (const label of labels) {
    if (typeof label !== 'string' || label.trim() === '') {
      return null;
    }
    names.push(label.trim());
  }
  return names;
}

export function readItemColors(
  entry: AuditLogEntry
): Array<string | null> | null {
  const itemIds = itemIdsOf(entry);
  const colors = entry.metadata?.itemColors;
  if (
    !Array.isArray(colors) ||
    colors.length !== itemIds.length ||
    itemIds.length === 0
  ) {
    return null;
  }
  return colors.map((color) => {
    if (typeof color !== 'string') {
      return null;
    }
    const trimmed = color.trim();
    return trimmed === '' ? null : trimmed;
  });
}

function recolorTarget(entry: AuditLogEntry): string | null {
  const changes = changesOf(entry);
  const body = changes.bodyColor;
  if (body) {
    const from = entityLabel(body.old);
    const to = entityLabel(body.new);
    if (to && to !== from) {
      return to;
    }
    if (!to && from) {
      const panelTo = entityLabel(changes.panelColor?.new);
      if (panelTo && panelTo !== from) {
        return panelTo;
      }
    }
    return null;
  }

  if (entry.subjectType !== 'StoleItem' || !changes.panelColor) {
    return null;
  }
  const from = entityLabel(changes.panelColor.old);
  const to = entityLabel(changes.panelColor.new);
  return to && to !== from ? to : null;
}

function isColorOnlyChange(entry: AuditLogEntry): boolean {
  const fields = Object.keys(changesOf(entry)).filter(
    (field) => !NOTE_NOISE_FIELDS.has(field)
  );
  return fields.length > 0 && fields.every((field) => COLOR_FIELDS.has(field));
}

function noteText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function noteLead(entry: AuditLogEntry): string | null {
  const changes = changesOf(entry);
  if (!changes.notes) {
    return null;
  }
  const others = Object.keys(changes).filter(
    (field) => field !== 'notes' && !NOTE_NOISE_FIELDS.has(field)
  );
  if (others.length > 0) {
    return null;
  }
  const previous = noteText(changes.notes.old);
  const next = noteText(changes.notes.new);
  if (!previous && next) {
    return 'Note added';
  }
  if (previous && next && previous !== next) {
    return 'Note updated';
  }
  if (previous && !next) {
    return 'Note removed';
  }
  return null;
}

export function itemEditSentence(
  entry: AuditLogEntry
): ItemEditSentence | null {
  if (
    entry.action !== AuditAction.EntityUpdated ||
    !isVestmentItemType(entry.subjectType)
  ) {
    return null;
  }
  const label = readHistoryLabel(entry);
  if (!label) {
    return null;
  }
  const after = readHistoryLabelAfter(entry);
  if (after && after !== label) {
    return {
      label,
      action: `${vestmentAction(entry.subjectType, 'Updated')} to`,
      color: readHistoryColor(entry),
      actionLabel: after,
      actionColor: readHistoryColorAfter(entry),
    };
  }
  const color = readHistoryColor(entry);
  const recolor = isColorOnlyChange(entry) ? recolorTarget(entry) : null;
  if (recolor) {
    return { label, action: `Recolored to ${recolor}`, color };
  }
  const note = noteLead(entry);
  if (note) {
    return { label, action: note, color };
  }
  return {
    label,
    action: vestmentAction(entry.subjectType, 'Updated'),
    color,
  };
}
